import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import CropLandscapeOutlinedIcon from '@mui/icons-material/CropLandscapeOutlined';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import { downloadMisShortLabelSheet } from '../../../store/store';

// The most short labels one sheet holds (five strips). The server enforces the same number
// (utils/packingListTemplate.js SHORT_LABELS_PER_SHEET) - this one only stops the person ticking a sixth.
export const SHORT_LABELS_PER_SHEET = 5;

// Which paper is in the printer: a convenience, kept in this browser. A5 until the person says otherwise, because it is the choice that
// cannot cut a label: on bigger paper A5's sheet is simply whole at the top of the page, where A4's on A5 paper loses its labels.
const PAPER_KEY = 'xms_pl_sheet_paper';
export const readPaper = () => {
  try {
    return localStorage.getItem(PAPER_KEY) === 'a4' ? 'a4' : 'a5';
  } catch (_) { return 'a5'; }
};
export const writePaper = (paper) => {
  try { localStorage.setItem(PAPER_KEY, paper); } catch (_) { /* a private window: it lasts as long as the screen */ }
};

// "Short labels sheet": tick up to five pallets of the list and print their short labels together, one under the
// other on a single sheet with a dashed cut line between them - on one side of the page only. The person says which paper is in the
// printer, because nothing else knows: the print window's paper is often not what the printer holds (see utils/packingListTemplate.js).
export default function ShortLabelSheetDialog({ open, onClose, packingListId, docNumber, pallets }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);

  const [selected, setSelected] = useState([]);   // pallet IDs
  const [busy, setBusy] = useState(false);
  const [paper, setPaper] = useState(readPaper);

  // every opening starts from nothing ticked
  useEffect(() => { if (open) { setSelected([]); setBusy(false); } }, [open]);

  const T = {
    BD:       isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.1)',
    TEXT_PRI: isDark ? 'rgba(255,255,255,0.9)'  : theme.palette.text.primary,
    TEXT_SEC: isDark ? 'rgba(255,255,255,0.5)'  : theme.palette.text.secondary,
    TEXT_TER: isDark ? 'rgba(255,255,255,0.3)'  : 'rgba(0,0,0,0.4)',
    ROW_ON:   isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
  };

  const full = selected.length >= SHORT_LABELS_PER_SHEET;
  const toggle = (palletId) => setSelected((cur) => {
    if (cur.includes(palletId)) return cur.filter((id) => id !== palletId);
    return cur.length >= SHORT_LABELS_PER_SHEET ? cur : [...cur, palletId];
  });

  const handlePrint = async () => {
    if (!selected.length || busy) return;
    setBusy(true);
    // in the order the pallets are listed, not the order they were ticked
    const palletIds = (pallets || []).map((p) => p.palletId).filter((id) => selected.includes(id));
    await dispatch(downloadMisShortLabelSheet({ authCtx, axiosGlobal, id: packingListId, docNumber, palletIds, paper }));
    setBusy(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" fullScreen={isXs}
      PaperProps={{ sx: { borderRadius: isXs ? 0 : '14px' }, 'data-testid': 'pl-short-sheet-dialog' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: { xs: 2, sm: 2.5 }, py: { xs: 1, sm: 1.75 }, borderBottom: `1px solid ${T.BD}` }}>
        <CropLandscapeOutlinedIcon sx={{ fontSize: 20, color: T.TEXT_SEC }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: T.TEXT_PRI, flex: 1 }}>
          {t('mis.plShortSheetTitle')}
        </Typography>
        {/* 44px on a phone: the smallest comfortable touch target */}
        <IconButton size="small" onClick={onClose} disabled={busy} aria-label={t('common.close')}
          sx={isXs ? { width: 44, height: 44 } : undefined}>
          <CloseIcon sx={{ fontSize: isXs ? 22 : 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 1.5, pb: 1, overflowY: 'auto', flex: 1 }}>
        <Typography sx={{ fontSize: '0.74rem', color: T.TEXT_SEC, mb: 1.25 }}>
          {t('mis.plShortSheetHelp', { max: SHORT_LABELS_PER_SHEET })}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: T.TEXT_PRI, flex: 1 }}>{t('mis.plShortSheetPaper')}</Typography>
          <ToggleButtonGroup size="small" exclusive value={paper} disabled={busy}
            onChange={(_, value) => { if (value) { setPaper(value); writePaper(value); } }} aria-label={t('mis.plShortSheetPaper')}>
            {['a5', 'a4'].map((value) => (
              <ToggleButton key={value} value={value} data-testid={`pl-short-sheet-paper-${value}`}
                sx={{ px: 1.75, py: 0.25, minHeight: isXs ? 44 : 0, minWidth: isXs ? 56 : 0, fontSize: '0.78rem', fontWeight: 700 }}>
                {value.toUpperCase()}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Typography data-testid="pl-short-sheet-paper-hint" sx={{ fontSize: '0.72rem', color: T.TEXT_SEC, mb: 1.5 }}>
          {t(paper === 'a4' ? 'mis.plShortSheetPaperHintA4' : 'mis.plShortSheetPaperHintA5')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <Typography data-testid="pl-short-sheet-count"
            sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: T.TEXT_TER }}>
            {t('mis.plShortSheetSelected', { count: selected.length, max: SHORT_LABELS_PER_SHEET })}
          </Typography>
          {selected.length > 0 && (
            <Button size="small" onClick={() => setSelected([])} disabled={busy}
              sx={{ fontSize: '0.68rem', textTransform: 'none', minWidth: 0, py: 0,
                ...(isXs ? { minHeight: 40, px: 1.5, fontSize: '0.8rem' } : {}) }}>
              {t('mis.plShortSheetClear')}
            </Button>
          )}
        </Box>
        {full && (
          <Typography sx={{ fontSize: '0.7rem', color: T.TEXT_SEC, mb: 0.75 }}>
            {t('mis.plShortSheetFull', { max: SHORT_LABELS_PER_SHEET })}
          </Typography>
        )}

        <Box sx={{ border: `1px solid ${T.BD}`, borderRadius: '10px', overflow: 'hidden' }}>
          {(pallets || []).map((p, i) => {
            const on = selected.includes(p.palletId);
            const locked = full && !on;
            const pcs = (p.items || []).reduce((a, it) => a + (Number(it.pcs) || 0), 0);
            const code = (p.items || []).map((it) => it.code).find(Boolean) || p.productCode || '';
            return (
              <Box key={p.palletId} component="label"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.25, minHeight: isXs ? 56 : 0,
                  cursor: locked ? 'not-allowed' : 'pointer',
                  opacity: locked ? 0.45 : 1, bgcolor: on ? T.ROW_ON : 'transparent',
                  borderBottom: i < pallets.length - 1 ? `1px solid ${T.BD}` : 'none' }}>
                <Checkbox size="small" checked={on} disabled={locked || busy} onChange={() => toggle(p.palletId)}
                  inputProps={{ 'data-testid': `pl-short-sheet-pallet-${i}`, 'aria-label': p.palletId }} />
                <Typography sx={{ width: 28, fontSize: '0.7rem', color: T.TEXT_TER, fontVariantNumeric: 'tabular-nums' }}>
                  {i + 1}
                </Typography>
                <Typography sx={{ fontSize: isXs ? '0.92rem' : '0.82rem', fontWeight: 700, color: T.TEXT_PRI, minWidth: 54 }}>{p.palletId}</Typography>
                {p.reference && (
                  <Typography sx={{ fontSize: isXs ? '0.8rem' : '0.72rem', fontFamily: 'monospace', color: T.TEXT_SEC }}>{p.reference}</Typography>
                )}
                <Typography noWrap sx={{ fontSize: isXs ? '0.78rem' : '0.7rem', fontFamily: 'monospace', color: T.TEXT_TER, flex: 1, minWidth: 0, textAlign: 'end' }}>
                  {code}
                </Typography>
                <Typography sx={{ fontSize: isXs ? '0.78rem' : '0.7rem', color: T.TEXT_SEC, minWidth: 54, textAlign: 'end', fontVariantNumeric: 'tabular-nums' }}>
                  {pcs} {t('mis.plColPcs')}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* On a phone the two buttons share the width, 48px tall, clear of the home-indicator bar. */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, px: { xs: 2, sm: 2.5 }, pt: 1.5,
        pb: { xs: 'max(12px, env(safe-area-inset-bottom))', sm: 1.5 }, borderTop: `1px solid ${T.BD}` }}>
        <Button onClick={onClose} disabled={busy}
          sx={{ textTransform: 'none', flex: { xs: 1, sm: 'none' }, minHeight: { xs: 48, sm: 0 } }}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={handlePrint} disabled={!selected.length || busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <PrintIcon sx={{ fontSize: 16 }} />}
          data-testid="pl-short-sheet-print"
          sx={{ textTransform: 'none', minWidth: 120, flex: { xs: 2, sm: 'none' }, minHeight: { xs: 48, sm: 0 } }}>
          {busy ? t('mis.plPreparingLabel') : t('mis.plShortSheetPrint')}
        </Button>
      </Box>
    </Dialog>
  );
}
